<script setup>
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue'
import {ChartTypeEnum} from  "@/components/explorer/visualizations/ChartTypeEnum.js"

//events raised by component
const emit = defineEmits(['updateChart'])

//Properties set by parent
const props = defineProps({
	chartType: {
		type: String,
		required: true
	},
	validChartTypes: {
		type: Array,
		required: true
	}
})
const navigationItems = ref([
	{ id: 0, label: '<i class="fa-solid f fa-chart-line"></i> Line', chartName: ChartTypeEnum.LINE.chartName, apiName: ChartTypeEnum.LINE.apiName, altName: null },
	{ id: 1, label: '<i class="fa-solid f fa-chart-column"></i> Column', chartName: ChartTypeEnum.BAR.chartName, apiName: ChartTypeEnum.BAR.apiName, altName: ChartTypeEnum.BARH.chartName },
	{ id: 2, label: '<i class="fa-solid f fa-map"></i> Map', chartName: ChartTypeEnum.MAP.chartName, apiName: ChartTypeEnum.MAP.apiName, altName: null },
	{ id: 3, label: '<i class="fa-solid f fa-chart-tree-map"></i> Treemap', chartName: ChartTypeEnum.TREEMAP.chartName, apiName: ChartTypeEnum.TREEMAP.apiName, altName: null },
	{ id: 4, label: '<i class="fa-solid f fa-diagram-sankey"></i> Sankey', chartName: ChartTypeEnum.SANKEY.chartName, apiName: ChartTypeEnum.SANKEY.apiName, altName: null },
	{ id: 5, label: '<i class="fa-regular fa-table"></i> Table', chartName: ChartTypeEnum.TABLE.chartName, apiName: ChartTypeEnum.TABLE.apiName, altName: null },
	{ id: 6, label: '<i class="fa-solid fa-book"></i> Data Notes', chartName: 'source', apiName: 'source', altName: null }
]);

//reactive field proxy
const type = ref('')
const containerRef = ref(null);
const ghostRef = ref(null);
const visibleItems = ref(navigationItems);
const show = ref(false)

//const visibleCount = ref(navigationItems.length);
const hiddenItems = ref([]);
const isOpen = ref(false);

//binding to invoke method on child componenet
const childRefVizMenu = ref({})
const childRefOptionsMenuToggle = ref({})

//local variables
const isMobile = () => window.innerWidth <= 767;

//methods
function vizMenuEventListener(e) {
	if (childRefVizMenu.value != null && !(childRefVizMenu.value.contains(e.target) ||
		childRefOptionsMenuToggle.value.contains(e.target))){
		toggleMenu();
	}
}

function updateType(t) {
	type.value = t
	//console.log('menu click')
	emit('updateChart', type.value);
	if(isMobile) toggleMenu(false);
}

function isDisabled(itemType) {
	if(itemType !== 'source' && props.validChartTypes && !props.validChartTypes.includes(itemType)) {
		return true
	}
}

function toggleMenu (expand) {
	calculateOverflow();
	if(childRefVizMenu.value && childRefVizMenu.value.classList) {
		if(expand == false) {
			childRefVizMenu.value.classList.remove('active');
		} else {
			childRefVizMenu.value.classList.toggle('active');
			isOpen.value = true;
		}
	}
	if(hiddenItems.value.length && expand && expand.value !== false && childRefVizMenu.value && childRefVizMenu.value.classList && childRefVizMenu.value.classList.contains('active')) {
		isOpen.value = true;
	}

	if(childRefVizMenu.value && childRefVizMenu.value.classList && childRefVizMenu.value.classList.contains('active')) {
		document.addEventListener('click', vizMenuEventListener);
		window.scrollTo(0, 0);
		let list = childRefVizMenu.value.querySelectorAll('button')
		let start = isMobile()?0:1
		for (let i = start; i < list.length; i++) {
			const el = list[i];
			if(el.disabled)
				continue;
			//focusedIndex = i
			el.focus();
			break;
		}
	} else {
		document.removeEventListener('click', vizMenuEventListener);
	}
}

function closeMenu() {
	if(childRefVizMenu.value.classList.contains('active')) {
    toggleMenu(false);
    childRefOptionsMenuToggle.value.focus();
		document.removeEventListener('click', vizMenuEventListener);
	}
}

function onEscape(event) {
  if (event.key === "Escape" && isMobile) {
    if(childRefVizMenu.value && childRefVizMenu.value.classList && childRefVizMenu.value.classList.contains('active')) {
      toggleMenu(false);
      childRefOptionsMenuToggle.value.focus();
      document.removeEventListener('click', vizMenuEventListener);
    }
  }
}

const handleKeydown = (e) => {
	const isOpen = childRefVizMenu.value && childRefVizMenu.value.classList && childRefVizMenu.value.classList.contains('active')
	let list = []
	try {
		list = Array.from(childRefVizMenu.value.querySelectorAll('button')).filter(btn => {
			const isBtnClose = btn.classList.contains('btn-close');
			const isDisabled = (btn).disabled;
			//Return true for all buttons EXCEPT the ones that are either 'btn-close' AND disabled
			return !(isBtnClose || isDisabled);
		});
	}catch(e) {	}

	if (!isOpen || list.length === 0) return

	let focusedIndex = Array.from(list).findIndex(button => document.activeElement === button);

	if (e.key === 'ArrowDown') {
		for(let x = focusedIndex + 1; x < (list.length + focusedIndex); x++) {
			let y = x % list.length
			if(list[y].disabled)
				continue
			list[y].focus()
			e.preventDefault()
			break;
		}
	} else if (e.key === 'ArrowUp') {
		for(let x = 0; x < list.length; x ++) {
			focusedIndex--;
			let y = ((focusedIndex % list.length) + list.length) % list.length;
			if(list[y].disabled)
				continue
			list[y].focus()
			e.preventDefault()
			break;
		}
	} else if (e.key === 'Enter') {
		if(e.target.id != "viz-menu-toggle") {
			const option = list[focusedIndex]
			if (option) option.click()
				e.preventDefault()
		}
	} else if (e.key === 'Escape') {
		isOpen.value = false
		e.preventDefault()
	}
}

const calculateOverflow = async() => {
	if(import.meta.env.VITE_APP_DEBUG)
		console.log('VizMenu: calculateOverflow')

	const el = containerRef.value;
	if (!el) return;

	// gather the component widths
	const gap = 52
	const moreWidth = document.querySelector('.vis-menu-container-shell').getBoundingClientRect().width + (gap/2)
  	const totWidth = el.getBoundingClientRect().width
	let sumItemsWidth = 0
	const currentItems = [...el.children[0].children]
	currentItems.forEach((item) => {
		sumItemsWidth += item.getBoundingClientRect().width
	})
	sumItemsWidth += ((currentItems.length - 1) * gap)

  	// Simple logic: if scrollWidth > offsetWidth, move an item to hidden
	if (el.scrollWidth > el.offsetWidth && visibleItems.value.length > 0) {
		hiddenItems.value.unshift(visibleItems.value.pop());
		await nextTick(calculateOverflow); // Recursive check until it fits
  	}
  	// If there's enough extra space, try to move items back to visible
  	else if (hiddenItems.value.length > 0 &&  totWidth > (sumItemsWidth + moreWidth)) {
    	// This part requires checking if the item + "More" button would fit
		//console.log('tot, sum:',totWidth ,sumItemsWidth + moreWidth)
		visibleItems.value.push(hiddenItems.value.shift());
  	}
};

let resizeObserver;

onMounted(async () => {
	document.addEventListener("keydown", onEscape);
  	document.addEventListener('keydown', handleKeydown)
	calculateOverflow();
	if (resizeObserver) {
		resizeObserver.disconnect()
	}
	
	resizeObserver = new ResizeObserver(calculateOverflow);
  	resizeObserver.observe(containerRef.value);
});

onBeforeUnmount(() => {
	document.removeEventListener("keydown", onEscape);
	document.removeEventListener('keydown', handleKeydown)
	if (resizeObserver) {
		resizeObserver.disconnect()
	}
});
if(import.meta.env.VITE_APP_DEBUG) {
	console.log('navigationItems',navigationItems.value)
}
</script>

<template>
	<div class="viz-menu-container" ref="containerRef">
		<ul class="vis-menu" id="viz-menu-expanded">
			<li class="vis-menu-item" v-for="(item, index) in visibleItems" :key="item.id">
				<span class="tooltip-wrapper"   :class="[
					{ 'tip-active': isDisabled(item.apiName) },
					index === 0
					? 'tooltip-left'
					: index === visibleItems.length - 1
						? 'tooltip-right'
						: 'tooltip-center']" data-tooltip="This format is unavailable based on indicator selected.">
				<button
					@click="updateType(item.chartName)"
					class="btn-tertiary" :disabled="isDisabled( item.apiName )" >
					<div v-html="item.label" class="menu-item" :class="{'active':[item.chartName,item.altName].includes(chartType)}"></div>
				</button>
				</span>
			</li>
			<!-- "More" Dropdown (Only shows if there are hidden items) -->
			<div v-if="hiddenItems.length" class="vis-menu-container-shell">
				<button id="viz-menu-toggle" ref="childRefOptionsMenuToggle" class="btn-tertiary" tabindex="0" @click="toggleMenu" @keydown.enter.prevent="toggleMenu">
					<i class="fa-regular fa-ellipsis"></i><span>More</span>
				</button>
				<div v-show="isOpen" class="viz-menu-container-dropdown" ref="childRefVizMenu">
					<div class="viz-menu-close">
						<button class="btn-icon btn-close" @click="closeMenu" aria-label="Close"><i class="fa-solid fa-times"></i></button>
					</div>
					<div v-for="item in hiddenItems" :key="item.id">
						<hr>
						<span class="tooltip-wrapper"   :class="[
							{ 'tip-active': isDisabled(item.apiName) },
							'tooltip-dropdown']" data-tooltip="This format is unavailable based on indicator selected.">
						<button
							@click="updateType(item.chartName)"
							class="btn-tertiary" :disabled="isDisabled( item.apiName )">
							<div v-html="item.label" class="menu-item" :class="{'active':[item.chartName,item.altName].includes(chartType)}"></div>
						</button>
					</span>
					</div>
				</div>
			</div>
		</ul>
		<!-- Hidden Ghost Menu to accurately measure vis-menu-container-shell element width -->
		<ul class="ghost-menu" ref="ghostRef">
			<div class="vis-menu-container-shell">
				<button id="ghost-menu-toggle" class="btn-tertiary">
					<i class="fa-regular fa-ellipsis"></i><span>More</span>
				</button>
			</div>
		</ul>
	</div>
</template>

<style scoped>
.viz-menu-container {
	width: 100%;
}
.vis-menu {
	display: flex;
	list-style: none;
	margin: 0;
	padding: 0;
	/*overflow: hidden; */
}
.vis-menu-item {
	list-style: none;
	white-space: nowrap;
}
/* Ghost Menu: Used purely for background width math */
.ghost-menu {
	position: absolute;
	visibility: hidden;
	pointer-events: none;
	display: flex;
	flex-direction: row;
	align-items: center;
	gap: 3.1875rem;
	overflow-x: auto;
}
#viz-menu-toggle {
	width: 4.5rem;
}
#viz-menu-expanded, #viz-menu-collapsed {
	position: relative;
	display: flex;
	flex-direction: column;
	width: 100%;
	align-items: stretch;
	gap: 3.1875rem;
	border-bottom: 1px solid var(--border-color-Divider, #A9B7C7);
	flex-direction: row;
	flex-wrap: nowrap;
	overflow-x: none;
}

#viz-menu-collapsed {
	display:none;
}

button.btn-tertiary {
	padding: 0 2px 0.75rem 0;
	font-family: var(--font-family-Title);
	color: var(--text-Primary);
	text-decoration: none;
	font-weight: 400;
	min-width: unset;
	height: 2.25rem;
	margin-bottom:.25rem;
}

button.btn-tertiary:focus {
	border-radius: 0;
	border-top-left-radius: var(----radius-md);
	border-top-right-radius: var(----radius-md);
	outline-offset: .125rem;;
}

button.btn-tertiary:hover {
	font-weight: 500;
	color: var(--text-Link);
	text-decoration: none;
	outline-offset: 0;
}

.btn-tertiary .menu-item {
	padding-bottom: .5rem;
	border-bottom:.25rem solid var(--button-tertiary-Default);
	white-space: nowrap;
}

.btn-tertiary .menu-item.active {
	border-bottom-color: var(--border-color-DividerActive, #064291);
	visibility: visible;
}

.btn-tertiary:disabled {
	border:0;
}
.btn-tertiary:disabled:hover {
	text-decoration: none;
	cursor:default;
}

.viz-menu-container-dropdown {
	flex-direction: column;
	display: none;
	position: absolute;
 	right: 0;
	top: 1.75rem;
	background: #fff;
	padding: 0;
	z-index: 2;
	border: 1px solid var(--border-color-input-Border, #A9B7C7);
}

#viz-menu-toggle {
	display:block;
	justify-self: end;
	min-height: unset;
	height: 2.5rem;
	padding-bottom: 0.875rem;
}

#viz-menu-toggle span {
	padding: 0 0.5rem;
	text-decoration: none;
	color: var(--text-MedGrey, #2c2c2c);
	cursor: pointer;
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-Base);
	font-weight: 400;
}

.viz-menu-container-dropdown .btn-tertiary{
	height: 2.5rem;
	min-width:unset;
	width: 100%;
	text-align: left;
	padding: 0.5rem 1rem;
	border-radius: 0;
	margin-bottom: 0;
	background-color: #fff;
}

hr {
	height: 1px;
	width:100%;
	background-color:var(--border-color-Divider, #A9B7C7);
	border: 0;
	margin: 0;
}

.viz-menu-container-dropdown .btn-tertiary:hover{
	background: var(--surface-color-Tertiary, #FAFAFA);
}

.viz-menu-container-dropdown .btn-tertiary:focus{
	border: 1.5px solid var(--border-color-divider-Active, #064291);
	outline: 0;
	border-radius: 0;
}

 .viz-menu-close {
	display: none;
}

/* 499px - Mobile phone */
@media (max-width: 31.1875rem) {
	/* technique to left align dropdown */
	.viz-menu-close {
		display: flex;
		width: 100%;
		height: 4.75rem;
		justify-content: end;
		align-items: center;
		padding: 1.25rem;
	}
	.vis-menu-container-shell {
		max-width: 500px;
		white-space: nowrap;
		margin: 0 auto;
		position: fixed;
		left: 0;
		width: 100%;
		z-index: 999;
	}
	.viz-menu-container-dropdown {
		margin: -7rem 0 0 0;
		width: 100%;
		text-align: left;
		position: sticky;
		left: 0;
		height: calc(100vh);
		width: 100vw;
		margin-left: calc(-50vw + 50%);
	}

	.viz-menu-container-dropdown button:last-child {
		border-bottom: 1px solid var(--border-color-Divider, #A9B7C7);
	}

	.viz-menu-container-dropdown .btn-tertiary{
		padding: var(--spacing-xl);
		height: 4.75rem;
		font-size: var(--font-size-H4)
	}
}

/* 780px - Max viz menu size when Settings panel displayed */
.viz-menu-container {
	container-type: inline-size;
}

/* 910px - responsive, small screens */
@container (max-width: 56.875rem) {

	#viz-menu-expanded {
		/*display: none;*/
	}

	#viz-menu-collapsed {
		display:flex;
    align-items: flex-start;
	}

	.viz-menu-container-dropdown.active {
		display:flex;
		/* min-width: 7.5rem; */
	}

	.viz-menu-col {
		max-width: 9rem;
		position: relative;
	}

	.viz-menu-col:last-child {
		max-width: unset;
		flex-grow: 1;
	}

	.viz-menu-container-dropdown .btn-tertiary .menu-item {
		padding-bottom: 0;
		/* border: 0; */
	}

	.btn-tertiary .menu-item.active {
		color: var(--text-Link, #08397a);
		/*border: 0;*/
		visibility: visible;
	}
	.viz-menu-container-dropdown .menu-item.active {
		border: 0;
	}

}

/*** tooltip classes ***/
.tooltip-wrapper {
	position: relative;
	pointer-events: auto; /* enable hover only when disabled */
}

.tooltip-wrapper.tip-active:hover::after {
  	content: attr(data-tooltip);
	font-family: var(--font-family-Title, Roboto);
	font-size: var(--font-size-Base, 1em);  
	position: absolute;
	top: -2.5rem;
	background: var(--brand-color-MedGrey);
	color: white;
	padding: .25rem .5rem;
	z-index:999;
	line-height:1.25rem;
	border-radius: .25rem;
}

/* First menu item */
.tooltip-wrapper.tooltip-left::after {
  left: 0%;
}

/* Middle menu items */
.tooltip-wrapper.tooltip-center::after {
  left: 50%;
  transform: translateX(-35%);
}

/* Last menu item */
.tooltip-wrapper.tooltip-right::after {
  right: -6.25rem;
}

/* dropdown tooltip */
.viz-menu-container-dropdown .tooltip-wrapper.tip-active[data-v-5e53824a]:hover::after {
	top: -3.75rem;
}

/* dropdown menu item */
.viz-menu-container-dropdown .tooltip-wrapper.tooltip-dropdown::after {
  left: -100%;
}

/* tooltip arrow */
.tooltip-wrapper.tip-active:hover::before {
    content: "";
    position: absolute;

    bottom: calc(100% - 2px); 
    left: 50%;
    transform: translateX(-50%) scale(1.6);

    border-width: 6px;
    border-style: solid;
    border-color: var(--brand-color-MedGrey) transparent transparent transparent;

    z-index: 1000;
}
</style>
<style>
	/* locks overlay so user cannot vertical scroll underlayer, must be outside of scoped style */
	@media (max-width: 31.1875rem) {
		body:has(.viz-menu-container-dropdown.active) {
			overflow-y: hidden;
			height: 100vh;
		}
	}
</style>
